import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getErrorMessage } from '../utils/errorHandler';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Users,
  Settings as SettingsIcon,
  Link,
  Plus,
  Trash2,
  Copy,
  Loader2,
  Upload,
  Database,
  AlertTriangle,
  Megaphone,
  Power,
  CheckCircle2,
  XCircle,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

const SettingsPage = () => {
  const { api, user: currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState({
    globalAutoAssign: false,
    assignmentRules: [],
    companyLogo: '',
    primaryColor: '#E31E24',
    metaVerifyToken: '',
    metaPageAccessToken: '',
    metaPageId: '',
    metaCampaigns: [],
    webhookUrl: '',
  });
  const [executives, setExecutives] = useState([]);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [showRuleDialog, setShowRuleDialog] = useState(false);
  const [showCampaignDialog, setShowCampaignDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Password change dialog state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [passwordChanged, setPasswordChanged] = useState(false);
  
  // Contact upload state
  const [r2gCount, setR2gCount] = useState(0);
  const [servallCount, setServallCount] = useState(0);
  const [uploading, setUploading] = useState({ r2g: false, servall: false });
  const [uploadResult, setUploadResult] = useState(null);
  const r2gFileRef = useRef(null);
  const servallFileRef = useRef(null);

  // Meta Page subscription state
  const [subscribing, setSubscribing] = useState(false);
  const [checkingSub, setCheckingSub] = useState(false);
  const [subscribeResult, setSubscribeResult] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [listingPages, setListingPages] = useState(false);
  const [pagesList, setPagesList] = useState(null);
  const [webhookLogs, setWebhookLogs] = useState(null);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const handleSyncLeads = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await api.post('/meta/sync-leads?limit_per_form=25');
      setSyncResult(res.data);
      if (res.data?.ok) {
        toast.success(`Synced — ${res.data.saved || 0} new lead(s) saved`);
      } else {
        toast.error(res.data?.error || 'Sync failed');
      }
    } catch (err) {
      setSyncResult({ ok: false, error: getErrorMessage(err) });
      toast.error(getErrorMessage(err));
    } finally {
      setSyncing(false);
    }
  };

  const fetchWebhookLogs = async () => {
    setLoadingLogs(true);
    try {
      const res = await api.get('/meta/webhook-logs?limit=20');
      setWebhookLogs(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleListPages = async () => {
    setListingPages(true);
    setPagesList(null);
    try {
      const res = await api.get('/meta/list-pages');
      setPagesList(res.data);
      if (res.data?.ok) {
        toast.success(`Found ${res.data.pages?.length || 0} page(s) accessible to this token`);
      } else {
        toast.error(res.data?.error || 'Could not list pages');
      }
    } catch (err) {
      setPagesList({ ok: false, error: getErrorMessage(err) });
      toast.error(getErrorMessage(err));
    } finally {
      setListingPages(false);
    }
  };

  const handleUsePageId = (id) => {
    setSettings((s) => ({ ...s, metaPageId: id }));
    toast.info('Page ID staged. Click "Save Integration Settings" to persist.');
  };

  const handleSubscribePage = async () => {
    setSubscribing(true);
    setSubscribeResult(null);
    try {
      const res = await api.post('/meta/subscribe-page');
      setSubscribeResult({ ok: !!res.data?.success, data: res.data });
      if (res.data?.success) {
        toast.success('Page subscribed to app for leadgen events');
      } else {
        toast.error(res.data?.error || 'Subscription failed');
      }
    } catch (err) {
      setSubscribeResult({ ok: false, data: { error: getErrorMessage(err) } });
      toast.error(getErrorMessage(err));
    } finally {
      setSubscribing(false);
    }
  };

  const handleCheckSubscription = async () => {
    setCheckingSub(true);
    setSubscriptionStatus(null);
    try {
      const res = await api.get('/meta/subscription-status');
      setSubscriptionStatus(res.data);
      if (res.data?.isSubscribed && res.data?.leadgenEnabled) {
        toast.success('Page is subscribed for leadgen ✓');
      } else if (res.data?.isSubscribed) {
        toast.warning('App subscribed but leadgen field missing');
      } else {
        toast.error('Page is NOT subscribed to Servall CRM Leads app');
      }
    } catch (err) {
      setSubscriptionStatus({ isSubscribed: false, error: getErrorMessage(err) });
      toast.error(getErrorMessage(err));
    } finally {
      setCheckingSub(false);
    }
  };

  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'Executive',
  });

  const [newRule, setNewRule] = useState({
    city: '',
    executiveId: '',
    executiveName: '',
  });

  const [newCampaign, setNewCampaign] = useState({
    campaignId: '',
    campaignName: '',
    isActive: true,
  });

  const isSuperAdmin = currentUser?.role === 'Super Admin';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, settingsRes, execRes] = await Promise.all([
        api.get('/users'),
        api.get('/settings'),
        api.get('/users/executives'),
      ]);
      setUsers(usersRes.data || []);
      setSettings(settingsRes.data || {});
      setExecutives(execRes.data || []);
      
      // Fetch contact counts
      await fetchContactCounts();
    } catch (err) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchContactCounts = async () => {
    try {
      const [r2gRes, servallRes] = await Promise.all([
        api.get('/contacts/r2g/count'),
        api.get('/contacts/servall/count'),
      ]);
      setR2gCount(r2gRes.data.count || 0);
      setServallCount(servallRes.data.count || 0);
    } catch (err) {
      console.error('Failed to fetch contact counts');
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/settings', {
        globalAutoAssign: settings.globalAutoAssign,
        assignmentRules: settings.assignmentRules,
        companyLogo: settings.companyLogo,
        primaryColor: settings.primaryColor,
        metaVerifyToken: settings.metaVerifyToken,
        metaPageAccessToken: settings.metaPageAccessToken,
        metaPageId: settings.metaPageId,
        metaCampaigns: settings.metaCampaigns,
      });
      toast.success('Settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      await api.post('/users', newUser);
      toast.success('User created');
      setShowUserDialog(false);
      setNewUser({ name: '', email: '', password: '', role: 'Executive' });
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to create user'));
    }
  };

  const handleUpdateUser = async (userId, data) => {
    try {
      await api.put(`/users/${userId}`, data);
      toast.success('User updated');
      fetchData();
    } catch (err) {
      toast.error('Failed to update user');
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    setDeleting(true);
    try {
      await api.delete(`/users/${userToDelete.id}`);
      toast.success(`User ${userToDelete.name} has been deleted`);
      setShowDeleteDialog(false);
      setUserToDelete(null);
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to delete user'));
    } finally {
      setDeleting(false);
    }
  };

  const confirmDeleteUser = (user) => {
    setUserToDelete(user);
    setShowDeleteDialog(true);
  };

  const handleToggleUserActive = async (user) => {
    await handleUpdateUser(user.id, { isActive: !user.isActive });
  };

  const handleToggleAutoAssign = async (user) => {
    // Only Super Admin can toggle Auto-Assign
    if (!isSuperAdmin) {
      toast.error('Only Super Admin can change Auto-Assign settings');
      return;
    }
    await handleUpdateUser(user.id, { autoAssignEnabled: !user.autoAssignEnabled });
  };

  // Generate random password
  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const openPasswordDialog = (user) => {
    setPasswordUser(user);
    const randomPwd = generateRandomPassword();
    setNewPassword(randomPwd);
    setGeneratedPassword('');
    setPasswordChanged(false);
    setShowPasswordDialog(true);
  };

  const handleChangePassword = async () => {
    if (!passwordUser || !newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await api.post(`/users/${passwordUser.id}/set-password`, { newPassword });
      setGeneratedPassword(newPassword);
      setPasswordChanged(true);
      toast.success(`Password changed for ${passwordUser.name}`);
    } catch (err) {
      toast.error('Failed to change password');
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(generatedPassword);
    toast.success('Password copied to clipboard!');
  };

  const handleResetPassword = async (userId) => {
    try {
      const response = await api.post(`/users/${userId}/reset-password`);
      toast.success(response.data.message);
    } catch (err) {
      toast.error('Failed to reset password');
    }
  };

  const handleAddRule = () => {
    if (!newRule.city || !newRule.executiveId) {
      toast.error('Please fill all fields');
      return;
    }
    const exec = executives.find((e) => e.id === newRule.executiveId);
    const rule = {
      ...newRule,
      executiveName: exec?.name || '',
    };
    setSettings({
      ...settings,
      assignmentRules: [...settings.assignmentRules, rule],
    });
    setShowRuleDialog(false);
    setNewRule({ city: '', executiveId: '', executiveName: '' });
  };

  const handleDeleteRule = (index) => {
    const rules = [...settings.assignmentRules];
    rules.splice(index, 1);
    setSettings({ ...settings, assignmentRules: rules });
  };

  // Campaign Management
  const handleAddCampaign = () => {
    if (!newCampaign.campaignName) {
      toast.error('Campaign Name is required');
      return;
    }
    const campaigns = [...(settings.metaCampaigns || []), newCampaign];
    setSettings({ ...settings, metaCampaigns: campaigns });
    setShowCampaignDialog(false);
    setNewCampaign({ campaignId: '', campaignName: '', isActive: true });
  };

  const handleToggleCampaign = (index) => {
    const campaigns = [...(settings.metaCampaigns || [])];
    campaigns[index] = { ...campaigns[index], isActive: !campaigns[index].isActive };
    setSettings({ ...settings, metaCampaigns: campaigns });
  };

  const handleDeleteCampaign = (index) => {
    const campaigns = [...(settings.metaCampaigns || [])];
    campaigns.splice(index, 1);
    setSettings({ ...settings, metaCampaigns: campaigns });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // Contact Upload Handlers
  const handleR2GUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.xlsx')) {
      toast.error('Please upload a CSV or Excel file');
      return;
    }
    
    setUploading({ ...uploading, r2g: true });
    setUploadResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/contacts/r2g/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      setUploadResult({ type: 'r2g', ...response.data });
      toast.success(`R2G: ${response.data.created} contacts uploaded, ${response.data.duplicates || 0} duplicates skipped`);
      await fetchContactCounts();
    } catch (err) {
      console.error('R2G upload error:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to upload R2G contacts';
      toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setUploading({ ...uploading, r2g: false });
      if (r2gFileRef.current) r2gFileRef.current.value = '';
    }
  };

  const handleServallUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.xlsx')) {
      toast.error('Please upload a CSV or Excel file');
      return;
    }
    
    setUploading({ ...uploading, servall: true });
    setUploadResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/contacts/servall/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      setUploadResult({ type: 'servall', ...response.data });
      toast.success(`Servall: ${response.data.created} contacts uploaded, ${response.data.duplicates || 0} duplicates skipped`);
      await fetchContactCounts();
    } catch (err) {
      console.error('Servall upload error:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to upload Servall contacts';
      toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setUploading({ ...uploading, servall: false });
      if (servallFileRef.current) servallFileRef.current.value = '';
    }
  };

  const handleClearContacts = async (type) => {
    if (!window.confirm(`Are you sure you want to delete ALL ${type.toUpperCase()} contacts? This cannot be undone.`)) {
      return;
    }
    
    try {
      await api.delete(`/contacts/${type}`);
      toast.success(`${type.toUpperCase()} contacts cleared`);
      await fetchContactCounts();
    } catch (err) {
      toast.error(`Failed to clear ${type} contacts`);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="settings-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[600px]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">
          Settings
        </h1>
        <p className="text-muted-foreground">
          Manage users, automation, integrations, and duplicate checking data
        </p>
      </div>

      <Tabs defaultValue="users" className="space-y-6">
        <TabsList>
          <TabsTrigger value="users" data-testid="users-tab">
            <Users className="mr-2 h-4 w-4" />
            Users
          </TabsTrigger>
          <TabsTrigger value="automation" data-testid="automation-tab">
            <SettingsIcon className="mr-2 h-4 w-4" />
            Automation
          </TabsTrigger>
          <TabsTrigger value="duplicates" data-testid="duplicates-tab">
            <Database className="mr-2 h-4 w-4" />
            Duplicate Check Data
          </TabsTrigger>
          <TabsTrigger value="integrations" data-testid="integrations-tab">
            <Link className="mr-2 h-4 w-4" />
            Meta Integration
          </TabsTrigger>
          <TabsTrigger value="campaigns" data-testid="campaigns-tab">
            <Megaphone className="mr-2 h-4 w-4" />
            Campaign Control
          </TabsTrigger>
        </TabsList>

        {/* User Management */}
        <TabsContent value="users">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Users</CardTitle>
                <CardDescription>Manage team members and their roles</CardDescription>
              </div>
              <Button onClick={() => setShowUserDialog(true)} data-testid="add-user-btn">
                <Plus className="mr-2 h-4 w-4" />
                Add User
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Auto-Assign</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Badge 
                          variant={user.role === 'Super Admin' ? 'default' : 'outline'}
                          className={user.role === 'Super Admin' ? 'bg-primary' : ''}
                        >
                          {user.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={user.isActive}
                          onCheckedChange={() => handleToggleUserActive(user)}
                          disabled={!isSuperAdmin && currentUser?.id !== user.id}
                          data-testid={`toggle-active-${user.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        {user.role === 'Executive' && (
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={user.autoAssignEnabled}
                              onCheckedChange={() => handleToggleAutoAssign(user)}
                              disabled={!isSuperAdmin}
                              data-testid={`toggle-auto-assign-${user.id}`}
                            />
                            {!isSuperAdmin && (
                              <span className="text-xs text-muted-foreground">
                                {user.autoAssignEnabled ? 'ON' : 'OFF'}
                              </span>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {isSuperAdmin && user.id !== currentUser?.id && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openPasswordDialog(user)}
                              data-testid={`change-password-${user.id}`}
                            >
                              Change Password
                            </Button>
                          )}
                          {isSuperAdmin && user.id !== currentUser?.id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              onClick={() => confirmDeleteUser(user)}
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Automation */}
        <TabsContent value="automation">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Auto-Assignment Rules</CardTitle>
                <CardDescription>
                  Define rules for automatic lead assignment based on city
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Enable Global Auto-Assign</Label>
                    <p className="text-sm text-muted-foreground">
                      Automatically assign new leads based on rules
                    </p>
                  </div>
                  <Switch
                    checked={settings.globalAutoAssign}
                    onCheckedChange={(v) => setSettings({ ...settings, globalAutoAssign: v })}
                    data-testid="global-auto-assign-switch"
                  />
                </div>

                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium">City-Based Rules</h4>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowRuleDialog(true)}
                      data-testid="add-rule-btn"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add Rule
                    </Button>
                  </div>

                  {settings.assignmentRules?.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No assignment rules configured
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {settings.assignmentRules?.map((rule, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-muted rounded-lg"
                        >
                          <div>
                            <span className="font-medium">{rule.city}</span>
                            <span className="mx-2 text-muted-foreground">→</span>
                            <span>{rule.executiveName}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteRule(index)}
                            data-testid={`delete-rule-${index}`}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Button onClick={handleSaveSettings} disabled={saving} data-testid="save-settings-btn">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Settings
            </Button>
          </div>
        </TabsContent>

        {/* PART A: Duplicate Check Data Upload */}
        <TabsContent value="duplicates">
          <div className="space-y-6">
            <Card className="border-orange-200 bg-orange-50/30">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-orange-600" />
                  <CardTitle className="text-orange-800">Meta Lead Duplicate Prevention</CardTitle>
                </div>
                <CardDescription className="text-orange-700">
                  Upload R2G and Servall contact lists. Meta leads will be checked against these databases 
                  before being saved. Duplicates are automatically rejected.
                </CardDescription>
              </CardHeader>
            </Card>

            {/* R2G Contacts Upload */}
            <Card data-testid="r2g-upload-panel">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Database className="h-5 w-5 text-primary" />
                      R2G Contacts
                    </CardTitle>
                    <CardDescription>
                      Upload R2G lead database for duplicate checking
                    </CardDescription>
                  </div>
                  <Badge variant="secondary" className="text-lg px-4 py-1">
                    {r2gCount.toLocaleString()} contacts
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <input
                    ref={r2gFileRef}
                    type="file"
                    accept=".csv,.xlsx"
                    onChange={handleR2GUpload}
                    className="hidden"
                    data-testid="r2g-file-input"
                  />
                  <Button
                    variant="outline"
                    onClick={() => r2gFileRef.current?.click()}
                    disabled={uploading.r2g}
                    className="flex-1"
                    data-testid="r2g-upload-btn"
                  >
                    {uploading.r2g ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="mr-2 h-4 w-4" />
                    )}
                    Upload R2G CSV/Excel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => handleClearContacts('r2g')}
                    data-testid="r2g-clear-btn"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Clear All
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  CSV must have columns: mobile (or phone, mobile_number), name (optional), city (optional)
                </p>
              </CardContent>
            </Card>

            {/* Servall Contacts Upload */}
            <Card data-testid="servall-upload-panel">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Database className="h-5 w-5 text-green-600" />
                      Servall Contacts
                    </CardTitle>
                    <CardDescription>
                      Upload Servall lead database for duplicate checking
                    </CardDescription>
                  </div>
                  <Badge variant="secondary" className="text-lg px-4 py-1">
                    {servallCount.toLocaleString()} contacts
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <input
                    ref={servallFileRef}
                    type="file"
                    accept=".csv,.xlsx"
                    onChange={handleServallUpload}
                    className="hidden"
                    data-testid="servall-file-input"
                  />
                  <Button
                    variant="outline"
                    onClick={() => servallFileRef.current?.click()}
                    disabled={uploading.servall}
                    className="flex-1"
                    data-testid="servall-upload-btn"
                  >
                    {uploading.servall ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="mr-2 h-4 w-4" />
                    )}
                    Upload Servall CSV/Excel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => handleClearContacts('servall')}
                    data-testid="servall-clear-btn"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Clear All
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  CSV must have columns: mobile (or phone, mobile_number), name (optional), city (optional)
                </p>
              </CardContent>
            </Card>

            {/* Upload Result */}
            {uploadResult && (
              <Card className="border-green-200 bg-green-50">
                <CardContent className="pt-6">
                  <h4 className="font-medium mb-2">
                    Upload Result ({uploadResult.type.toUpperCase()})
                  </h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Created:</span>
                      <span className="ml-2 font-medium text-green-600">{uploadResult.created}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Duplicates:</span>
                      <span className="ml-2 font-medium text-orange-600">{uploadResult.duplicates}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Errors:</span>
                      <span className="ml-2 font-medium text-red-600">{uploadResult.errors}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Meta Integration */}
        <TabsContent value="integrations">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Meta (Facebook) Lead Ads Integration</CardTitle>
                <CardDescription>
                  Connect your Facebook Lead Ads to automatically import leads.
                  Leads are checked against R2G and Servall databases before being saved.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-muted rounded-lg">
                  <Label className="mb-2 block">Webhook URL</Label>
                  <div className="flex gap-2">
                    <Input
                      value={settings.webhookUrl || 'Configure in Meta Business Suite'}
                      readOnly
                      data-testid="webhook-url"
                    />
                    <Button
                      variant="outline"
                      onClick={() => copyToClipboard(settings.webhookUrl)}
                      disabled={!settings.webhookUrl}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Use this URL in your Meta webhook configuration
                  </p>
                </div>

                <div>
                  <Label htmlFor="verifyToken">Verify Token</Label>
                  <Input
                    id="verifyToken"
                    type="password"
                    value={settings.metaVerifyToken || ''}
                    onChange={(e) => setSettings({ ...settings, metaVerifyToken: e.target.value })}
                    placeholder="Enter your verify token"
                    data-testid="verify-token-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    This token is the <strong>single source of truth</strong>. Use the exact same value in the Meta Developer Console webhook configuration.
                  </p>
                </div>

                <div>
                  <Label htmlFor="accessToken">Page Access Token</Label>
                  <Input
                    id="accessToken"
                    type="password"
                    value={settings.metaPageAccessToken || ''}
                    onChange={(e) => setSettings({ ...settings, metaPageAccessToken: e.target.value })}
                    placeholder="Enter your page access token"
                    data-testid="access-token-input"
                  />
                  <p className="text-xs mt-1 text-amber-700 dark:text-amber-500 flex items-start gap-1" data-testid="access-token-warning">
                    <span aria-hidden="true">⚠</span>
                    <span>Keep this token confidential. Rotate it regularly from the Meta Developer Console — anyone with this token can read your leads.</span>
                  </p>
                </div>

                <div className="p-4 border rounded-lg bg-primary/5 border-primary/20 space-y-3">
                  <h4 className="font-medium text-primary">Meta Account Info:</h4>
                  <div className="text-sm space-y-1">
                    <p><strong>Business Portfolio ID:</strong> 155185803531198</p>
                    <p><strong>Ad Account (Servall L):</strong> 1247951080708529</p>
                    <p><strong>Ad Account (Servall.india):</strong> 449164053569329</p>
                  </div>

                  <div className="pt-2 border-t border-primary/20">
                    <Label htmlFor="metaPageId">Facebook Page ID</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        id="metaPageId"
                        type="text"
                        value={settings.metaPageId || ''}
                        onChange={(e) => setSettings({ ...settings, metaPageId: e.target.value.trim() })}
                        placeholder="e.g. 109018465011811"
                        data-testid="page-id-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleListPages}
                        disabled={listingPages || !settings.metaPageAccessToken}
                        data-testid="list-pages-btn"
                      >
                        {listingPages ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Database className="mr-2 h-4 w-4" />
                        )}
                        List My Pages
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      The Facebook Page that will receive lead webhooks. Use "List My Pages" to discover all Pages your saved token can access. Click "Use this ID" next to the correct row, then click "Save Integration Settings".
                    </p>
                  </div>

                  {pagesList && (
                    <div className="mt-2 p-3 border rounded-md bg-white" data-testid="pages-list-result">
                      {pagesList.ok ? (
                        <>
                          <p className="text-xs font-medium mb-2">
                            Token kind: <code className="bg-muted px-1 rounded">{pagesList.tokenKind}</code>
                            {pagesList.tokenSubject?.name && (
                              <> &middot; Subject: {pagesList.tokenSubject.name} ({pagesList.tokenSubject.id})</>
                            )}
                          </p>
                          {(!pagesList.pages || pagesList.pages.length === 0) ? (
                            <p className="text-xs text-amber-700">
                              This token does not have access to any Pages. The user that generated the token must be Admin/Editor on a Facebook Page.
                            </p>
                          ) : (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="text-xs">Page Name</TableHead>
                                  <TableHead className="text-xs">Page ID</TableHead>
                                  <TableHead className="text-xs">Action</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {pagesList.pages.map((p) => (
                                  <TableRow key={p.id} data-testid={`page-row-${p.id}`}>
                                    <TableCell className="text-xs">
                                      {p.name}
                                      {p.category && (
                                        <span className="text-muted-foreground"> &middot; {p.category}</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-xs">
                                      <code className="bg-muted px-1 rounded">{p.id}</code>
                                      {p.matchesConfigured && (
                                        <Badge variant="outline" className="ml-2 text-[10px]">configured</Badge>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant={p.matchesConfigured ? 'outline' : 'default'}
                                        onClick={() => handleUsePageId(p.id)}
                                        data-testid={`use-page-${p.id}`}
                                      >
                                        {p.matchesConfigured ? 'In Use' : 'Use this ID'}
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                          {pagesList.hint && (
                            <p className="text-xs text-muted-foreground mt-2">{pagesList.hint}</p>
                          )}
                        </>
                      ) : (
                        <div className="text-xs text-red-700">
                          <p className="font-medium">Could not list pages</p>
                          <p className="mt-1 break-words">
                            {typeof pagesList.error === 'string'
                              ? pagesList.error
                              : JSON.stringify(pagesList.error)}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Page Subscription Controls */}
                <div className="p-4 border rounded-lg space-y-3" data-testid="page-subscription-section">
                  <div>
                    <h4 className="font-medium mb-1">Page Subscription</h4>
                    <p className="text-xs text-muted-foreground">
                      Subscribe Servall India Page (1503421039731588) to Servall CRM Leads app (882347181354560) for <code className="bg-muted px-1 rounded">leadgen</code> events. Required for Meta Lead Ads webhooks to fire.
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      onClick={handleSubscribePage}
                      disabled={subscribing || !settings.metaPageAccessToken}
                      data-testid="subscribe-page-btn"
                    >
                      {subscribing ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Link className="mr-2 h-4 w-4" />
                      )}
                      Subscribe Page to App
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCheckSubscription}
                      disabled={checkingSub || !settings.metaPageAccessToken}
                      data-testid="check-subscription-btn"
                    >
                      {checkingSub ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="mr-2 h-4 w-4" />
                      )}
                      Check Subscription Status
                    </Button>
                  </div>

                  {!settings.metaPageAccessToken && (
                    <p className="text-xs text-amber-700 dark:text-amber-500">
                      Save a Page Access Token above before using these actions.
                    </p>
                  )}

                  {subscribeResult && (
                    <div
                      className={`p-3 rounded-md border text-sm ${
                        subscribeResult.ok
                          ? 'border-green-200 bg-green-50 text-green-800'
                          : 'border-red-200 bg-red-50 text-red-800'
                      }`}
                      data-testid="subscribe-result"
                    >
                      <div className="flex items-start gap-2">
                        {subscribeResult.ok ? (
                          <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                        ) : (
                          <XCircle className="h-4 w-4 mt-0.5 shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">
                            {subscribeResult.ok
                              ? subscribeResult.data?.message || 'Subscription successful'
                              : `Subscription failed${subscribeResult.data?.errorCode ? ` (code ${subscribeResult.data.errorCode})` : ''}`}
                          </p>
                          {!subscribeResult.ok && (
                            <p className="text-xs mt-1 break-words">
                              {typeof subscribeResult.data?.error === 'string'
                                ? subscribeResult.data.error
                                : JSON.stringify(subscribeResult.data?.error)}
                            </p>
                          )}
                          {!subscribeResult.ok && subscribeResult.data?.isPermissionIssue && (
                            <div className="mt-2 space-y-1" data-testid="permission-hint">
                              <p className="text-xs font-medium">How to fix:</p>
                              <p className="text-xs">{subscribeResult.data.hint}</p>
                              <p className="text-xs font-medium mt-1">Required permissions on Page Access Token:</p>
                              <ul className="text-xs list-disc list-inside">
                                {(subscribeResult.data.requiredPermissions || []).map((p) => (
                                  <li key={p}><code className="bg-red-100 px-1 rounded">{p}</code></li>
                                ))}
                              </ul>
                              <p className="text-xs mt-1">
                                Generate token from{' '}
                                <a
                                  href="https://developers.facebook.com/tools/explorer/"
                                  target="_blank"
                                  rel="noreferrer"
                                  className="underline font-medium"
                                >
                                  Graph API Explorer
                                </a>{' '}
                                → select your App → "Get Page Access Token" → select Servall India Page → add ALL permissions above.
                              </p>
                            </div>
                          )}
                          {subscribeResult.ok && subscribeResult.data?.subscribedFields && (
                            <p className="text-xs mt-1">
                              Fields: {subscribeResult.data.subscribedFields.join(', ')}
                            </p>
                          )}
                          {subscribeResult.ok && subscribeResult.data?.tokenAutoUpgraded && (
                            <p className="text-xs mt-1 italic">
                              ℹ Auto-derived a Page-level token from your User token and saved it. Future requests will reuse this Page token directly.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {subscriptionStatus && (
                    <div
                      className={`p-3 rounded-md border text-sm ${
                        subscriptionStatus.isSubscribed && subscriptionStatus.leadgenEnabled
                          ? 'border-green-200 bg-green-50 text-green-800'
                          : 'border-amber-200 bg-amber-50 text-amber-800'
                      }`}
                      data-testid="subscription-status-result"
                    >
                      <div className="flex items-start gap-2">
                        {subscriptionStatus.isSubscribed && subscriptionStatus.leadgenEnabled ? (
                          <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                        ) : (
                          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                        )}
                        <div className="flex-1 min-w-0 space-y-1">
                          <p className="font-medium">
                            {subscriptionStatus.isSubscribed
                              ? subscriptionStatus.leadgenEnabled
                                ? 'Subscribed ✓ — leadgen events will reach the CRM'
                                : 'App subscribed but leadgen field missing'
                              : 'Not subscribed'}
                          </p>
                          {subscriptionStatus.appName && (
                            <p className="text-xs">App: {subscriptionStatus.appName} ({subscriptionStatus.appId})</p>
                          )}
                          {subscriptionStatus.subscribedFields && (
                            <p className="text-xs">
                              Subscribed fields: {subscriptionStatus.subscribedFields.join(', ') || '(none)'}
                            </p>
                          )}
                          {subscriptionStatus.hint && (
                            <p className="text-xs">{subscriptionStatus.hint}</p>
                          )}
                          {subscriptionStatus.error && (
                            <p className="text-xs break-words">
                              {typeof subscriptionStatus.error === 'string'
                                ? subscriptionStatus.error
                                : JSON.stringify(subscriptionStatus.error)}
                            </p>
                          )}
                          {subscriptionStatus.isPermissionIssue && (
                            <div className="mt-2" data-testid="status-permission-hint">
                              <p className="text-xs font-medium">Required permissions on Page Access Token:</p>
                              <ul className="text-xs list-disc list-inside">
                                {(subscriptionStatus.requiredPermissions || []).map((p) => (
                                  <li key={p}><code className="bg-amber-100 px-1 rounded">{p}</code></li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="p-4 border rounded-lg bg-purple-50 border-purple-200" data-testid="app-roles-helper">
                  <h4 className="font-medium text-purple-800 mb-2">🧪 Add App Testers (Development Mode)</h4>
                  <p className="text-sm text-purple-900 mb-3">
                    While the app is in Development Mode (before App Review approval), webhooks will only fire for leads submitted by users you've added as <strong>App Admins / Developers / Testers</strong>. Add your team's Facebook accounts to test the full lead flow now.
                  </p>
                  <ol className="text-sm text-purple-900 list-decimal list-inside space-y-1 mb-3">
                    <li>Open Meta App Dashboard → Roles → Roles (link below)</li>
                    <li>Click <strong>"Add Administrators"</strong>, <strong>"Add Developers"</strong>, or <strong>"Add Testers"</strong></li>
                    <li>Enter the team member's Facebook username / UID and send invite</li>
                    <li>They accept the invite on Facebook → role activates immediately</li>
                    <li>Submit a test lead from THEIR Facebook account → webhook will fire → lead appears in CRM</li>
                  </ol>
                  <Button
                    type="button"
                    asChild
                    variant="outline"
                    data-testid="open-app-roles-btn"
                  >
                    <a
                      href="https://developers.facebook.com/apps/882347181354560/roles/roles/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open App Roles Page ↗
                    </a>
                  </Button>
                  <p className="text-xs text-purple-700 mt-2">
                    💡 Testers don't need App Review. This is the fastest way to validate the full integration end-to-end before submitting for App Review.
                  </p>
                </div>

                <div className="p-4 border rounded-lg space-y-3 bg-emerald-50 border-emerald-200" data-testid="sync-leads-section">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <h4 className="font-medium text-emerald-800">🔄 Manual Sync Meta Leads (Interim)</h4>
                      <p className="text-xs text-emerald-900 mt-1">
                        Webhooks not firing yet (Dev Mode / App Review pending)? Use this button to <strong>pull recent leads directly from Graph API</strong> for every Lead Form on the configured Page. Same campaign filter, duplicate check, and auto-assignment logic as the webhook.
                      </p>
                    </div>
                    <Button
                      type="button"
                      onClick={handleSyncLeads}
                      disabled={syncing || !settings.metaPageAccessToken}
                      data-testid="sync-leads-btn"
                    >
                      {syncing ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="mr-2 h-4 w-4" />
                      )}
                      Sync Meta Leads
                    </Button>
                  </div>

                  {syncResult && (
                    <div
                      className={`p-3 rounded-md border text-sm ${
                        syncResult.ok
                          ? 'border-emerald-300 bg-white text-emerald-900'
                          : 'border-red-200 bg-red-50 text-red-800'
                      }`}
                      data-testid="sync-leads-result"
                    >
                      {syncResult.ok ? (
                        <>
                          <p className="font-medium">
                            <CheckCircle2 className="h-4 w-4 inline mr-1" />
                            Sync complete — {syncResult.saved || 0} new lead(s) saved
                          </p>
                          <div className="flex flex-wrap gap-3 text-xs mt-2">
                            <span>📋 Forms scanned: <strong>{syncResult.totalFormsScanned}</strong></span>
                            <span>📥 Leads fetched: <strong>{syncResult.totalLeadsFetched}</strong></span>
                            <span className="text-green-700">✓ Saved: <strong>{syncResult.saved}</strong></span>
                            <span className="text-amber-700">⊘ Duplicates: <strong>{syncResult.rejectedDuplicate}</strong></span>
                            <span className="text-amber-700">⊘ Campaign filtered: <strong>{syncResult.rejectedCampaignNotActive}</strong></span>
                            <span className="text-muted-foreground">⏭ Skipped: <strong>{syncResult.skipped}</strong></span>
                            {syncResult.errors > 0 && (
                              <span className="text-red-700">✕ Errors: <strong>{syncResult.errors}</strong></span>
                            )}
                          </div>
                          {syncResult.perForm && syncResult.perForm.length > 0 && (
                            <details className="mt-2 text-xs">
                              <summary className="cursor-pointer underline">Per-form breakdown</summary>
                              <Table className="mt-2">
                                <TableHeader>
                                  <TableRow>
                                    <TableHead className="text-xs">Form</TableHead>
                                    <TableHead className="text-xs">Fetched</TableHead>
                                    <TableHead className="text-xs">Saved</TableHead>
                                    <TableHead className="text-xs">Dup</TableHead>
                                    <TableHead className="text-xs">Filtered</TableHead>
                                    <TableHead className="text-xs">Error</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {syncResult.perForm.map((f) => (
                                    <TableRow key={f.formId}>
                                      <TableCell className="text-xs">{f.formName || f.formId}</TableCell>
                                      <TableCell className="text-xs">{f.fetched}</TableCell>
                                      <TableCell className="text-xs text-green-700">{f.saved}</TableCell>
                                      <TableCell className="text-xs">{f.rejectedDuplicate}</TableCell>
                                      <TableCell className="text-xs">{f.rejectedCampaignNotActive}</TableCell>
                                      <TableCell className="text-xs text-red-700 break-words max-w-xs">
                                        {f.error || (f.errors > 0 ? `${f.errors} error(s)` : '—')}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </details>
                          )}
                        </>
                      ) : (
                        <>
                          <p className="font-medium">
                            <XCircle className="h-4 w-4 inline mr-1" />
                            Sync failed
                          </p>
                          <p className="text-xs mt-1 break-words">
                            {typeof syncResult.error === 'string' ? syncResult.error : JSON.stringify(syncResult.error)}
                          </p>
                          {syncResult.hint && (
                            <p className="text-xs mt-1">{syncResult.hint}</p>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>

                <div className="p-4 border rounded-lg space-y-3" data-testid="webhook-activity-section">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <h4 className="font-medium">Webhook Activity (Live Debug)</h4>
                      <p className="text-xs text-muted-foreground">
                        Every incoming Meta webhook is logged here. Use this to verify Meta is actually hitting <code className="bg-muted px-1 rounded">/api/meta-webhook</code> when you submit a test lead.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={fetchWebhookLogs}
                      disabled={loadingLogs}
                      data-testid="refresh-webhook-logs-btn"
                    >
                      {loadingLogs ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="mr-2 h-4 w-4" />
                      )}
                      Refresh
                    </Button>
                  </div>

                  {webhookLogs && (
                    <div className="space-y-2" data-testid="webhook-logs-result">
                      <div className="flex gap-3 text-xs">
                        <span><strong>Last 24h:</strong> {webhookLogs.summary?.totalReceived24h ?? 0} received</span>
                        <span><strong>Saved:</strong> {webhookLogs.summary?.successfullySaved24h ?? 0}</span>
                        <span><strong>All-time:</strong> {webhookLogs.summary?.totalReceivedAllTime ?? 0}</span>
                      </div>

                      {webhookLogs.logs?.length === 0 ? (
                        <div className="p-3 border rounded-md bg-amber-50 border-amber-200 text-xs text-amber-800">
                          <AlertTriangle className="h-4 w-4 inline mr-1" />
                          <strong>No webhook calls received yet.</strong> This means Meta is NOT hitting your CRM.
                          Common reasons: Page not subscribed correctly, wrong Page ID configured, or test lead created on a different Page. Check Meta App Dashboard → Webhooks → Recent Activity.
                        </div>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="text-xs">Received</TableHead>
                              <TableHead className="text-xs">Page ID</TableHead>
                              <TableHead className="text-xs">Leadgen ID</TableHead>
                              <TableHead className="text-xs">Outcome</TableHead>
                              <TableHead className="text-xs">Detail</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {webhookLogs.logs.map((log) => {
                              const ok = log.outcome === 'saved';
                              const reject = log.outcome?.startsWith('rejected_');
                              const err = log.outcome?.startsWith('error_');
                              const tone = ok ? 'text-green-700' : reject ? 'text-amber-700' : err ? 'text-red-700' : 'text-muted-foreground';
                              return (
                                <TableRow key={log._logId} data-testid={`webhook-log-${log._logId}`}>
                                  <TableCell className="text-xs whitespace-nowrap">
                                    {new Date(log.receivedAt).toLocaleString()}
                                  </TableCell>
                                  <TableCell className="text-xs">
                                    <code className="bg-muted px-1 rounded">{log.pageId || '—'}</code>
                                  </TableCell>
                                  <TableCell className="text-xs">
                                    <code className="bg-muted px-1 rounded">{log.leadgenId || '—'}</code>
                                  </TableCell>
                                  <TableCell className={`text-xs font-medium ${tone}`}>
                                    {log.outcome || log.processingStatus || '—'}
                                  </TableCell>
                                  <TableCell className="text-xs break-words max-w-md">
                                    {log.outcomeDetail?.hint || log.outcomeDetail?.foundIn || log.outcomeDetail?.mobile || log.outcomeDetail?.leadId || log.outcomeDetail?.error || '—'}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                  )}
                </div>

                <div className="p-4 border rounded-lg bg-blue-50 border-blue-200">
                  <h4 className="font-medium text-blue-800 mb-2">How Meta Lead Flow Works:</h4>
                  <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                    <li>User submits lead form on Facebook/Instagram</li>
                    <li>Meta sends webhook to this CRM</li>
                    <li>CRM checks if campaign is enabled (Campaign Control tab)</li>
                    <li>CRM fetches full lead details from Meta API v21.0</li>
                    <li>Mobile number is checked against R2G & Servall databases</li>
                    <li>If DUPLICATE or CAMPAIGN DISABLED: Lead is rejected and logged</li>
                    <li>If UNIQUE: Lead is saved with campaign & ad account info</li>
                  </ol>
                </div>
              </CardContent>
            </Card>

            <Button onClick={handleSaveSettings} disabled={saving} data-testid="save-integration-btn">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Integration Settings
            </Button>
          </div>
        </TabsContent>

        {/* Campaign Control */}
        <TabsContent value="campaigns">
          <div className="space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Meta Campaign Control</CardTitle>
                  <CardDescription>
                    Only leads from ACTIVE campaigns will be captured. Add campaigns and toggle them on/off.
                  </CardDescription>
                </div>
                <Button onClick={() => setShowCampaignDialog(true)} data-testid="add-campaign-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Campaign
                </Button>
              </CardHeader>
              <CardContent>
                {(!settings.metaCampaigns || settings.metaCampaigns.length === 0) ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Megaphone className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No campaigns configured yet.</p>
                    <p className="text-sm">Add campaigns to control which Meta leads are captured.</p>
                    <p className="text-sm mt-2 text-orange-600">
                      ⚠️ If no campaigns are added, ALL Meta leads will be accepted.
                    </p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Campaign Name</TableHead>
                        <TableHead>Campaign ID</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {settings.metaCampaigns.map((campaign, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{campaign.campaignName}</TableCell>
                          <TableCell>
                            <code className="text-xs bg-muted px-2 py-1 rounded">
                              {campaign.campaignId || 'Not specified'}
                            </code>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={campaign.isActive}
                                onCheckedChange={() => handleToggleCampaign(index)}
                                data-testid={`toggle-campaign-${index}`}
                              />
                              <Badge variant={campaign.isActive ? 'default' : 'secondary'}>
                                {campaign.isActive ? (
                                  <><Power className="h-3 w-3 mr-1" /> Active</>
                                ) : (
                                  'Disabled'
                                )}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteCampaign(index)}
                              data-testid={`delete-campaign-${index}`}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            <Button onClick={handleSaveSettings} disabled={saving} data-testid="save-campaigns-btn">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Campaign Settings
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add User Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>Create a new team member account</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="newUserName">Name</Label>
              <Input
                id="newUserName"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                data-testid="new-user-name"
              />
            </div>
            <div>
              <Label htmlFor="newUserEmail">Email</Label>
              <Input
                id="newUserEmail"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                data-testid="new-user-email"
              />
            </div>
            <div>
              <Label htmlFor="newUserPassword">Password</Label>
              <Input
                id="newUserPassword"
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                data-testid="new-user-password"
              />
            </div>
            <div>
              <Label htmlFor="newUserRole">Role</Label>
              <Select
                value={newUser.role}
                onValueChange={(v) => setNewUser({ ...newUser, role: v })}
              >
                <SelectTrigger data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Executive">Executive</SelectItem>
                  <SelectItem value="Manager">Manager</SelectItem>
                  <SelectItem value="CEO">CEO</SelectItem>
                  <SelectItem value="Super Admin">Super Admin</SelectItem>
                  <SelectItem value="R2G">R2G (Duplicate Check Only)</SelectItem>
                </SelectContent>
              </Select>
              {newUser.role === 'R2G' && (
                <p className="text-xs text-muted-foreground mt-1">
                  R2G users can only access the Duplicate Check dashboard
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateUser} data-testid="create-user-btn">
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Rule Dialog */}
      <Dialog open={showRuleDialog} onOpenChange={setShowRuleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Assignment Rule</DialogTitle>
            <DialogDescription>
              Leads from this city will be automatically assigned
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="ruleCity">City</Label>
              <Input
                id="ruleCity"
                value={newRule.city}
                onChange={(e) => setNewRule({ ...newRule, city: e.target.value })}
                placeholder="e.g., Pune"
                data-testid="rule-city"
              />
            </div>
            <div>
              <Label htmlFor="ruleExecutive">Assign To</Label>
              <Select
                value={newRule.executiveId}
                onValueChange={(v) => setNewRule({ ...newRule, executiveId: v })}
              >
                <SelectTrigger data-testid="rule-executive">
                  <SelectValue placeholder="Select Executive" />
                </SelectTrigger>
                <SelectContent>
                  {executives.map((exec) => (
                    <SelectItem key={exec.id} value={exec.id}>{exec.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRuleDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddRule} data-testid="add-rule-confirm-btn">
              Add Rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Campaign Dialog */}
      <Dialog open={showCampaignDialog} onOpenChange={setShowCampaignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Meta Campaign</DialogTitle>
            <DialogDescription>
              Add a campaign to control which Meta leads are captured
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="campaignName">Campaign Name *</Label>
              <Input
                id="campaignName"
                value={newCampaign.campaignName}
                onChange={(e) => setNewCampaign({ ...newCampaign, campaignName: e.target.value })}
                placeholder="e.g., Servall Franchise 2024"
                data-testid="campaign-name"
              />
            </div>
            <div>
              <Label htmlFor="campaignId">Campaign ID (Optional)</Label>
              <Input
                id="campaignId"
                value={newCampaign.campaignId}
                onChange={(e) => setNewCampaign({ ...newCampaign, campaignId: e.target.value })}
                placeholder="e.g., 23849571234567890"
                data-testid="campaign-id"
              />
              <p className="text-xs text-muted-foreground mt-1">
                If provided, matching will be stricter. Otherwise, name matching will be used.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="campaignActive"
                checked={newCampaign.isActive}
                onCheckedChange={(v) => setNewCampaign({ ...newCampaign, isActive: v })}
              />
              <Label htmlFor="campaignActive">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCampaignDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddCampaign} data-testid="add-campaign-confirm-btn">
              Add Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{userToDelete?.name}</strong>? 
              This action cannot be undone. The user will lose all access immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setUserToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={(open) => {
        if (!open) {
          setShowPasswordDialog(false);
          setPasswordUser(null);
          setNewPassword('');
          setGeneratedPassword('');
          setPasswordChanged(false);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>
              Set a new password for <strong>{passwordUser?.name}</strong> ({passwordUser?.email})
            </DialogDescription>
          </DialogHeader>
          
          {!passwordChanged ? (
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="newPassword">New Password</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="newPassword"
                    type="text"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    data-testid="new-password-input"
                  />
                  <Button
                    variant="outline"
                    onClick={() => setNewPassword(generateRandomPassword())}
                    type="button"
                  >
                    Generate
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Password must be at least 6 characters
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800 font-medium mb-2">✅ Password Changed Successfully!</p>
                <p className="text-sm text-muted-foreground mb-3">
                  Share this password with <strong>{passwordUser?.name}</strong>:
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-3 bg-white border rounded font-mono text-lg tracking-wide">
                    {generatedPassword}
                  </code>
                  <Button variant="outline" onClick={copyPassword} data-testid="copy-password-btn">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-orange-600 mt-3">
                  ⚠️ This password will not be shown again. Please copy and share it now.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            {!passwordChanged ? (
              <>
                <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleChangePassword} data-testid="confirm-password-change-btn">
                  Change Password
                </Button>
              </>
            ) : (
              <Button onClick={() => setShowPasswordDialog(false)}>
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
